import { useEffect } from "react";
import { Col, Row, Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import FloatingButtons from "./FloatingButtons";
import { GiSpeedBoat } from "react-icons/gi";
import { MdOutlineAnchor } from "react-icons/md";

const benefits = [
  { icon: "💰", title: "Earn Income", desc: "Your boat earns money while you're not using it. Set your own rates and availability." },
  { icon: "✅", title: "Verified Renters", desc: "Bass boat renters go through BBR's verification process so you know who's on your boat." },
  { icon: "🔒", title: "Secure Payments", desc: "Instant checkout and secure payment processing — funds deposited directly to you." },
  { icon: "📱", title: "iOS & Android Apps", desc: "Manage your listings, bookings, and messages from anywhere on our mobile apps." },
  { icon: "💬", title: "Direct Messaging", desc: "Communicate securely with renters through our built-in messaging system." },
  { icon: "📅", title: "Easy Calendar Management", desc: "Block off dates, set availability windows, and stay in control of your schedule." },
  { icon: "🎯", title: "You Choose Who Rents", desc: "Review renter profiles and accept or decline bookings — full control, always." },
  { icon: "📈", title: "Start with 10% commission", desc: "Reduce your commission to 0% with our pro staff referral system." },
  { icon: "🤖", title: "24/7 Chatbot Support", desc: "Our AI-powered chatbot is always available to assist you and your renters." },
];

export default function ListBoatForIncome() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <FloatingButtons />

      {/* Hero */}
      <section className="cs-bg-dark py-5 cs-section" style={{ borderBottom: "1px solid #1e1e1e" }}>
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <p
                className="fw-bold text-uppercase mb-3"
                style={{ color: "#ddb467", letterSpacing: "4px", fontSize: "0.78rem" }}
              >
                For Boat Owners
              </p>
              <h1 className="fw-bold text-white mb-4" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)", lineHeight: 1.15 }}>
                Put Your Boat To Work.{" "}
                <span style={{ color: "#ddb467" }}>Start Earning Today.</span>
              </h1>
              <p className="text-secondary mb-4" style={{ fontSize: "1.05rem" }}>
                When you're not out on the water, your boat can be generating income — all on your terms.
              </p>
              <p className="text-secondary mb-4" style={{ fontSize: "1.05rem" }}>All boat types, We list tournament ready bass boats, pontoons, wakeboats, jet skis and whatever other boat you’d like to make income from.</p>
              <p className="text-secondary mb-5" style={{ fontSize: "1.05rem" }}>
                We handle the booking infrastructure. You keep control of your pricing, availability, and who you rent to.
              </p>
              <Link
                to="/auth/signup-owner"
                className="btn btn-lg fw-bold text-uppercase px-5 py-3"
                style={{
                  backgroundColor: "#ddb467",
                  color: "#000",
                  borderRadius: "4px",
                  letterSpacing: "1px",
                  fontSize: "0.95rem",
                }}
              >
                Free to Sign Up →
              </Link>
              <p className="text-secondary mt-3" style={{ fontSize: "0.85rem" }}>
                No subscription fees. No hidden charges. Just results.
              </p>
            </Col>
            <Col lg={6}>
              <img
                src="/assets/frontend/images/statsBg.webp"
                alt="BBR Bass Boat on the water"
                className="img-fluid rounded shadow-lg"
                style={{ width: "100%", height: "420px", objectFit: "cover" }}
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5" style={{ backgroundColor: "#0d0d0d" }}>
        <Container>
          <Row className="justify-content-center text-center mb-5">
            <Col lg={7}>
              <h2 className="fw-bold text-white text-uppercase mb-3">
                How BBR Works For{" "}
                <span style={{ color: "#ddb467" }}>Boat Owners</span>
              </h2>
              <div style={{ width: "60px", height: "3px", backgroundColor: "#ddb467", margin: "0 auto" }} />
            </Col>
          </Row>
          <Row className="g-4 justify-content-center">
            {[
              { step: "01", title: "Create Your Free Account", desc: "Sign up as a boat owner in minutes. No credit card required." },
              { step: "02", title: "List Your Boat", desc: "Add photos, set your daily rate, define availability and rental rules." },
              { step: "03", title: "Accept Bookings & Get Paid", desc: "Verified renters book your boat. You approve and get paid securely." },
            ].map((s) => (
              <Col key={s.step} lg={4} md={6}>
                <div
                  className="h-100 p-4 rounded"
                  style={{ backgroundColor: "#111", border: "1px solid #2a2a2a" }}
                >
                  <p className="fw-bold mb-2" style={{ color: "#ddb467", fontSize: "2rem", fontFamily: "monospace" }}>
                    {s.step}
                  </p>
                  <h5 className="text-white fw-bold mb-2">{s.title}</h5>
                  <p className="text-secondary mb-0" style={{ fontSize: "0.9rem" }}>{s.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Benefits */}
      <section className="py-5" style={{ backgroundColor: "#111" }}>
        <Container>
          <Row className="justify-content-center text-center mb-5">
            <Col lg={7}>
              <h2 className="fw-bold text-white text-uppercase mb-3">
                Everything You Need to{" "}
                <span style={{ color: "#ddb467" }}>Succeed</span>
              </h2>
              <p className="text-secondary">BBR gives boat owners a complete toolkit to earn income and manage their rental business effortlessly.</p>
            </Col>
          </Row>
          <Row className="g-4">
            {benefits.map((b, i) => (
              <Col key={i} lg={4} md={6}>
                <div
                  className="d-flex align-items-start gap-3 p-3 rounded"
                  style={{ backgroundColor: "#0d0d0d", border: "1px solid #222" }}
                >
                  <span style={{ fontSize: "1.6rem", flexShrink: 0 }}>{b.icon}</span>
                  <div>
                    <h6 className="text-white fw-bold mb-1">{b.title}</h6>
                    <p className="text-secondary mb-0" style={{ fontSize: "0.85rem" }}>{b.desc}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Stats Banner */}
      <section className="py-5" style={{ backgroundColor: "#ddb467" }}>
        <Container>
          <Row className="text-center g-4 justify-content-center">
            {[
              { value: "2021", label: "Founded" },
              { value: "10%", label: "Starting Commission" },
              { value: "USA", label: "Nationwide Platform" },
              { value: "Free", label: "To List Your Boat" },
            ].map((stat, i) => (
              <Col key={i} xs={6} md={3}>
                <h2 className="fw-bold text-black mb-0" style={{ fontSize: "2.5rem" }}>{stat.value}</h2>
                <p className="text-black fw-bold text-uppercase mb-0" style={{ fontSize: "0.75rem", letterSpacing: "2px" }}>{stat.label}</p>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-5 cs-bg-dark" style={{ borderTop: "1px solid #1e1e1e" }}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <MdOutlineAnchor style={{ fontSize: "48px", color: "#ddb467", marginBottom: "16px" }} />
              <h2 className="fw-bold text-white mb-3 text-uppercase">
                Ready to Start Earning?
              </h2>
              <p className="text-secondary mb-4" style={{ maxWidth: "520px", margin: "0 auto 24px" }}>
                Listing your boat on BBR is completely free. Sign up today, create your listing, and start accepting bookings.
              </p>
              <Link
                to="/auth/signup-owner"
                className="btn btn-lg fw-bold text-uppercase px-5 py-3"
                style={{
                  backgroundColor: "#ddb467",
                  color: "#000",
                  borderRadius: "4px",
                  letterSpacing: "1px",
                  fontSize: "0.95rem",
                }}
              >
                Free to Sign Up →
              </Link>
              <p className="text-secondary mt-3 mb-0" style={{ fontSize: "0.85rem" }}>
                Already have an account?{" "}
                <Link to="/auth" className="fw-bold" style={{ color: "#ddb467", textDecoration: "underline" }}>
                  Log In
                </Link>
              </p>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

